import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'

// apis
import { fetchSalesPayment, updateSalesPayment } from '@/apis/aipass'

// components
import { SettingPaymentEditForm } from '@/components/organisms/settings/sales-manager/payment/edit-form'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AccountsReceivable } from '@/models/accounts-receivable'

type ContainerProps = {}

export const SettingSalesManagerPaymentEdit: React.FC<ContainerProps> = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()

  const [paymentSettings, setPaymentSettings] = useState<any>()
  const [paymentList, setPaymentList] = useState<any>()
  const [deteledPaymentSettings, setDeteledPaymentSettings] = useState<any>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const history = useHistory()
  const location = useLocation()

  const onChangeState = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>, index) => {
    const value = e.target.value
    const name = e.target.name
    const newPaymentSettings = [...paymentSettings]
    newPaymentSettings[index][name] = value
    setPaymentSettings(newPaymentSettings)
  }

  const deletePaymentSetting = (id: number, index: number) => {
    // Remove deleted elements from the displayed array
    const newPaymentSettings = [...paymentSettings]
    newPaymentSettings.splice(index, 1)
    if (id) {
      const newDeteledPaymentSettings = [...deteledPaymentSettings, { id: id }]
      setDeteledPaymentSettings(newDeteledPaymentSettings)
    }
    setPaymentSettings(newPaymentSettings)
  }

  const addPaymentSetting = () => {
    const newPaymentSetting = {
      methodCode: '',
      name: '',
      commissionRate: '',
      isAccountsReceivable: AccountsReceivable.AccountsReceivable,
      tax: '',
      taxClass: '',
      displayOrder: Math.max(0, ...(paymentSettings || []).map(p => Number(p.displayOrder) || 0)) + 1,
    }
    setPaymentSettings([...paymentSettings, newPaymentSetting])
  }

  const onSavePaymentSettings = async () => {
    setIsLoading(true)
    try {
      let fixedPaymentSettings = JSON.parse(JSON.stringify(paymentSettings))
      // Compare with data before change and delete from fixed array if the contents are the same
      paymentList.forEach(payment => {
        const fixedPaymentIndex = fixedPaymentSettings.findIndex(item => item?.id === payment.id)
        if (fixedPaymentIndex === -1) return

        const fixedPayment = fixedPaymentSettings[fixedPaymentIndex]
        if (
          payment.name === fixedPayment.name &&
          payment.commissionRate === fixedPayment.commissionRate &&
          payment.methodCode === fixedPayment.methodCode &&
          payment.isAccountsReceivable === fixedPayment.isAccountsReceivable &&
          payment.tax === fixedPayment.tax &&
          payment.taxClass === fixedPayment.taxClass &&
          payment.displayOrder === fixedPayment.displayOrder
        ) {
          fixedPaymentSettings.splice(fixedPaymentIndex, 1)
        }
      })

      fixedPaymentSettings = fixedPaymentSettings.map(fixePaymentSetting => {
        fixePaymentSetting.commissionRate = Number(fixePaymentSetting.commissionRate)
        fixePaymentSetting.tax = Number(fixePaymentSetting.tax)
        if (fixePaymentSetting.isAccountsReceivable) {
          fixePaymentSetting.isAccountsReceivable = Number(fixePaymentSetting.isAccountsReceivable)
        }
        return fixePaymentSetting
      })

      // Join id of deleted data
      fixedPaymentSettings = [...fixedPaymentSettings, ...deteledPaymentSettings]
      await updateSalesPayment(fixedPaymentSettings)
      history.push({ pathname: '/setting/sales-manager/payment', search: location.search })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const _fetchSalesPayment = async () => {
    setIsLoading(true)
    try {
      const res = await fetchSalesPayment()
      if (!res) {
        alert(t('Communication failed'))
        return
      }
      if (!res.salesPayment) {
        console.warn('salesPayment is empty')
        setPaymentSettings([])
        setPaymentList([])
        return
      }

      // 支払い方法「-」の表示順は常に1で固定
      const salesPayment = res.salesPayment.map(payment => (payment.name === '-' ? { ...payment, displayOrder: 1 } : payment))
      setPaymentSettings(salesPayment)
      // Deposit setting list before change
      setPaymentList(JSON.parse(JSON.stringify(res.salesPayment)))
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/sales-manager/payment',
    onAllowed: _fetchSalesPayment,
  })

  const footerContent = <EditFooter onSave={onSavePaymentSettings} onCancel={history.goBack} />

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <>
        <div css={settingPaymentEditContainerStyle}>
          <div css={settingPaymentEditHeaderStyle}>
            <p>{t('Payment')}</p>
          </div>
          {paymentSettings && (
            <SettingPaymentEditForm
              paymentSettings={paymentSettings}
              paymentList={paymentList || []}
              onChangeState={onChangeState}
              deletePaymentSetting={deletePaymentSetting}
              addPaymentSetting={addPaymentSetting}
            />
          )}
        </div>
      </>
    </SettingsLayout>
  )
}

const settingPaymentEditContainerStyle = css({
  height: '100%',
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: 5,
  background: '#FFF',
})

const settingPaymentEditHeaderStyle = css({
  height: 50,
  padding: '16px 32px',
  borderBottom: '1px solid #F2F2F2',
  p: {
    fontSize: 18,
    fontWeight: 'bold',
  },
})
